/*
  PEDLOCK MINI SALE – the theme's only script. No libraries.
  Everything here improves pages that already work without it:
  - Homepage "Available products": asks Shopify for the server-rendered,
    availability-filtered results section and puts it in place.
  - Related products under a product, the same way.
  - Add to cart without leaving the page, cart count, confirmation.
  - Product option buttons, gallery thumbnails, quantity steppers.
  - Menu panel on phones and tablets (a native <dialog>).
  - Search suggestions limited to products in stock, rendered by Shopify
    with the same price logic as the product cards.
  - Sort menu and cart quantities submit themselves.
*/
(() => {
  const config = window.miniSale || { routes: {}, strings: {} };
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  /* ---------- Money ---------- */

  // Same output as Liquid's money_without_trailing_zeros for the shop's format
  const formatMoney = (cents) => {
    const format = config.moneyFormat || '₹{{amount}}';
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const amount = decimals === '00' ? withCommas : `${withCommas}.${decimals}`;
    const amountComma = decimals === '00' ? withDots : `${withDots},${decimals}`;
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, withDots)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amountComma)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/<[^>]*>/g, '');
  };

  /* ---------- Server-rendered sections (homepage grid, related products) ---------- */

  const loadHomepageResults = () => {
    $$('[data-results-url]').forEach(async (slot) => {
      try {
        const url = slot.dataset.resultsUrl;
        const early = window.miniSaleEarly && window.miniSaleEarly[url];
        const html = early
          ? await early
          : await fetch(url).then((response) => {
            if (!response.ok) throw new Error(response.status);
            return response.text();
          });
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const results = $('[data-results]', doc);
        if (!results) throw new Error('no results');
        slot.replaceChildren(...results.childNodes);
        const label = results.dataset.countLabel;
        const count = $('[data-results-count]', slot.closest('section'));
        if (count && label) {
          count.textContent = label;
          count.hidden = false;
        }
      } catch (error) {
        const fallback = $('noscript', slot);
        slot.innerHTML = fallback ? fallback.textContent : '';
      }
    });
  };

  const loadRecommendations = () => {
    $$('[data-recommendations]').forEach((section) => {
      const load = async () => {
        try {
          const response = await fetch(section.dataset.url);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = $('[data-recommendations]', doc);
          if (fresh && fresh.innerHTML.trim()) section.innerHTML = fresh.innerHTML;
        } catch (error) {
          /* Related products are optional */
        }
      };
      if (!('IntersectionObserver' in window)) return load();
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          load();
        }
      }, { rootMargin: '400px 0px' });
      observer.observe(section);
    });
  };

  /* ---------- Cart ---------- */

  const toast = $('[data-toast]');
  let toastTimer;
  const showToast = (text) => {
    if (!toast) return;
    $('[data-toast-text]', toast).textContent = text;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { toast.hidden = true; }, 6000);
  };

  const updateCartCount = async () => {
    try {
      const cart = await (await fetch(`${config.routes.cart}.js`)).json();
      $$('[data-cart-count]').forEach((badge) => {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      });
    } catch (error) {
      /* The count refreshes on the next page */
    }
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-add-form]');
    if (!form || !window.fetch) return;
    // "Buy it now" posts its own form; only intercept Add to cart
    if (event.submitter && event.submitter.name === 'checkout') return;
    event.preventDefault();

    const buttons = [$('[data-add-button]', form)];
    if (event.submitter && event.submitter.matches('[data-sticky-button]')) buttons.push(event.submitter);
    const active = buttons.filter(Boolean).map((button) => ({ button, label: button.textContent }));
    const error = $('[data-form-error]', form);
    active.forEach(({ button }) => {
      button.disabled = true;
      button.textContent = config.strings.adding;
    });
    if (error) error.hidden = true;

    try {
      const response = await fetch(`${config.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || config.strings.cartError);
      showToast(`${config.strings.added}: ${data.product_title || data.title}`);
      updateCartCount();
    } catch (problem) {
      if (error) {
        error.textContent = problem.message || config.strings.cartError;
        error.hidden = false;
      } else {
        showToast(problem.message || config.strings.cartError);
      }
    } finally {
      active.forEach(({ button, label }) => {
        button.disabled = false;
        button.textContent = label;
      });
    }
  });

  /* ---------- Quantity steppers, sort, cart quantities ---------- */

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-qty-step]');
    if (!step) return;
    const input = $('.qty__input', step.closest('.qty'));
    const min = Number(input.min || 0);
    const next = Math.max(min, (Number(input.value) || 0) + Number(step.dataset.qtyStep));
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  let cartTimer;
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-autosubmit]')) event.target.form.submit();
    if (event.target.matches('[data-cart-qty]')) {
      clearTimeout(cartTimer);
      const form = event.target.form;
      cartTimer = setTimeout(() => form.submit(), 500);
    }
  });

  /* ---------- Bottom bars (phones and tablets) ---------- */

  // Shows the bar only while the page's own button is off screen, so the
  // same button never appears twice
  const followButton = (bar, target, onlyAfter) => {
    if (!bar || !target || !('IntersectionObserver' in window)) {
      if (bar) bar.classList.add('is-visible');
      return;
    }
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && (!onlyAfter || entry.boundingClientRect.top < 0);
      bar.classList.toggle('is-visible', show);
      document.body.classList.toggle('has-sticky-bar', show);
    }).observe(target);
  };

  /* ---------- Product page ---------- */

  const initProduct = (section) => {
    const dataNode = $('[data-variants]', section);
    const form = $('[data-product-form]', section);
    if (!dataNode || !form) return;
    const variants = JSON.parse(dataNode.textContent);
    const select = $('[data-variant-select]', form);
    const button = $('[data-add-button]', form);
    const fieldsets = $$('.option', form);
    const track = $('[data-gallery-track]', section);
    const stickyBar = $('[data-sticky-buy]', section);
    const stickyButton = $('[data-sticky-button]', section);
    const stickyPrice = $('[data-sticky-price]', section);

    const selectedOptions = () => fieldsets.map((fieldset) => {
      const checked = $('input:checked', fieldset);
      return checked ? checked.value : null;
    });

    // Same markup and rounding as snippets/price.liquid and discount-percent.liquid
    const priceHtml = (variant, size) => {
      const compare = variant.compare_at_price || 0;
      let percent = 0;
      if (compare > variant.price) percent = Math.round(((compare - variant.price) * 100) / compare);
      const onSale = percent >= 1;
      const hidden = (text) => `<span class="visually-hidden">${text}</span>`;
      return `<div class="price price--${size}${onSale ? ' price--on-sale' : ''}" data-price>
          <span class="price__now">${hidden(onSale ? config.strings.salePrice : config.strings.regularPrice)}${formatMoney(variant.price)}</span>
          ${onSale ? `<s class="price__was">${hidden(config.strings.regularPrice)}${formatMoney(compare)}</s>
          <span class="price__off">${config.strings.percentOff.replace('[percent]', percent)}</span>` : ''}
        </div>`;
    };

    const renderPrice = (variant) => {
      if (stickyPrice) stickyPrice.innerHTML = priceHtml(variant, 'card');
      const slot = $('[data-price-slot]', section);
      if (!slot) return;
      slot.innerHTML = priceHtml(variant, 'large');
    };

    const renderStock = (variant) => {
      const stock = $('[data-stock]', section);
      if (!stock) return;
      const threshold = config.lowStockThreshold || 0;
      const low = variant.available && variant.tracked && threshold > 0 && variant.quantity > 0 && variant.quantity <= threshold;
      stock.className = `stock${variant.available ? (low ? ' stock--low' : '') : ' stock--out'}`;
      const text = !variant.available
        ? config.strings.soldOut
        : low ? config.strings.onlyLeft.replace('[count]', variant.quantity) : config.strings.inStock;
      stock.innerHTML = `<span class="stock__dot" aria-hidden="true"></span>${text}`;
    };

    // Strike out values with no available variant, given the other choices
    const markUnavailable = () => {
      const current = selectedOptions();
      fieldsets.forEach((fieldset, index) => {
        $$('.option__input', fieldset).forEach((input) => {
          const probe = current.slice();
          probe[index] = input.value;
          const match = variants.find((variant) => variant.options.every((value, i) => value === probe[i]));
          const label = input.nextElementSibling;
          if (label) label.classList.toggle('is-unavailable', !match || !match.available);
        });
        const shown = $('[data-option-selected]', fieldset);
        if (shown) shown.textContent = current[index] || '';
      });
    };

    const onChange = () => {
      const current = selectedOptions();
      const variant = variants.find((item) => item.options.every((value, i) => value === current[i]));
      markUnavailable();
      if (!variant) {
        [button, stickyButton].filter(Boolean).forEach((target) => {
          target.disabled = true;
          target.textContent = config.strings.unavailable;
        });
        return;
      }
      if (select) select.value = String(variant.id);
      [button, stickyButton].filter(Boolean).forEach((target) => {
        target.disabled = !variant.available;
        target.textContent = variant.available ? config.strings.addToCart : config.strings.soldOut;
      });
      renderPrice(variant);
      renderStock(variant);
      if (variant.media_id && track) {
        const slide = $(`[data-media-id="${variant.media_id}"]`, track);
        if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

    fieldsets.forEach((fieldset) => fieldset.addEventListener('change', onChange));
    if (fieldsets.length) markUnavailable();

    // The bottom bar appears once the visitor has scrolled past the main button
    followButton(stickyBar, $('.product-form__row', form), true);
  };

  const initGallery = (gallery) => {
    const track = $('[data-gallery-track]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const counter = $('[data-gallery-count]', gallery);
    if (!track || !thumbs.length) return;
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const slide = $(`[data-media-id="${thumb.dataset.galleryThumb}"]`, track);
        if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
    });
    // Updates only when the visible image changes, so scrolling stays cheap
    let current = 0;
    track.addEventListener('scroll', () => {
      const index = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
      if (index === current) return;
      current = index;
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));
      if (counter) counter.textContent = `${index + 1} / ${thumbs.length}`;
    }, { passive: true });
  };

  /* ---------- Search ---------- */

  const initSearch = () => {
    const toggle = $('[data-search-toggle]');
    const panel = $('[data-search-panel]');
    if (!toggle || !panel) return;
    const input = $('[data-predictive-input]', panel);
    const results = $('[data-predictive-results]', panel);

    const setOpen = (open) => {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open && input) input.focus();
    };
    toggle.addEventListener('click', () => setOpen(panel.hidden));
    // A tap outside the header closes the panel
    document.addEventListener('click', (event) => {
      if (!panel.hidden && !event.target.closest('[data-header]')) setOpen(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });

    if (!input || !results || !config.routes.predictiveSearch) return;
    let timer;
    let controller;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = setTimeout(async () => {
        if (controller) controller.abort();
        controller = new AbortController();
        const params = new URLSearchParams({
          q: term,
          'resources[type]': 'product',
          'resources[limit]': '6',
          'resources[options][unavailable_products]': 'hide',
          'resources[options][fields]': 'title,product_type,variants.title,vendor',
          section_id: 'predictive-search',
        });
        try {
          const response = await fetch(`${config.routes.predictiveSearch}?${params}`, { signal: controller.signal });
          if (!response.ok) throw new Error(response.status);
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = $('[data-predictive-content]', doc);
          results.replaceChildren(...(content ? [content] : []));
        } catch (error) {
          /* Typing again retries */
        }
      }, 220);
    });
  };

  /* ---------- Menu panel ---------- */

  const initMenu = () => {
    const menu = $('[data-menu]');
    const openButton = $('[data-menu-open]');
    if (!menu || !openButton || typeof menu.showModal !== 'function') return;
    openButton.addEventListener('click', () => menu.showModal());
    $('[data-menu-close]', menu)?.addEventListener('click', () => menu.close());
    // A tap on the dimmed page beside the panel closes it
    menu.addEventListener('click', (event) => {
      if (event.target === menu) menu.close();
    });
  };


  /* ---------- Start ---------- */

  document.documentElement.classList.add('js');
  loadHomepageResults();
  loadRecommendations();
  initSearch();
  initMenu();
  followButton($('[data-sticky-checkout]'), $('[data-main-checkout]'));
  $$('[data-product]').forEach(initProduct);
  $$('[data-gallery]').forEach(initGallery);
})();
